import { Box, CircularProgress, Typography } from '@mui/material'
import { ModelContext } from 'context/model/ModelContext'
import { MouseEvent, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRootNodesQuery } from '../../apollo/configurator/queries/RootNodes.generated'
import ImportModelButton from '../../components/modelManager/molecules/manager/ImportModelButton'
import NewModelButton from '../../components/modelManager/molecules/manager/NewModelButton'
import {
    BulkDeleteConfirmDialog,
    DownloadModelDialog,
    DuplicateModelDialog,
    ModelListBulkActions,
    ModelListFilters,
    ModelListPagination,
    ModelListTable
} from './components'
import { useModelFiltering, useModelSelection } from './utils'

const ModelList = () => {
    const { data, loading, error, refetch } = useRootNodesQuery()
    const navigate = useNavigate()
    const { setState } = useContext(ModelContext)

    const [downloadModelId, setDownloadModelId] = useState<string | null>(null)
    const [duplicateModelId, setDuplicateModelId] = useState<string | null>(null)

    const {
        filteredAndSortedModels,
        paginatedModels,
        searchFilter,
        setSearchFilter,
        statusFilter,
        setStatusFilter,
        order,
        orderBy,
        handleRequestSort,
        handleChangePage,
        handleChangeRowsPerPage,
        page,
        rowsPerPage
    } = useModelFiltering({
        models: data?.configuratorNodes || []
    })

    const {
        selected,
        handleSelectAllClick,
        handleSelectClick,
        isSelected,
        handleBulkDelete,
        isDeleting,
        showConfirmDialog,
        selectedModelNames,
        handleConfirmDelete,
        handleCancelDelete
    } = useModelSelection({
        paginatedModels,
        filteredAndSortedModels,
        refetch,
        allModels: data?.configuratorNodes || []
    })

    const handleExportClick = (e: MouseEvent<HTMLButtonElement>, id: string) => {
        e?.stopPropagation()
        setDownloadModelId(id)
    }

    const handleCloseDownloadDialog = () => {
        setDownloadModelId(null)
    }

    const handleDuplicateClick = (e: MouseEvent<HTMLButtonElement>, id: string) => {
        e?.stopPropagation()
        setDuplicateModelId(id)
    }

    const handleCloseDuplicateDialog = () => {
        setDuplicateModelId(null)
    }

    const handleRowClick = (id: string) => {
        const model = data?.configuratorNodes.find((node) => node.id === id)

        if (model) {
            setState({
                currentModel: id,
                currentModelCode: model.code || '',
                hasChanges: false,
                hasEdited: false,
                updatedModel: false,
                showEditedMessage: false
            })

            navigate(`/model/${model.code}`)
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Box>
                <Typography color="error">Error loading models: {error.message}</Typography>
            </Box>
        )
    }

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <ModelListFilters
                    searchFilter={searchFilter}
                    setSearchFilter={setSearchFilter}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                />

                <Box display="flex" gap={1} ml="auto">
                    <NewModelButton />
                    <ImportModelButton />
                </Box>

                <ModelListBulkActions
                    selectedCount={selected.length}
                    onBulkDelete={handleBulkDelete}
                    isDeleting={isDeleting}
                />
            </Box>

            <ModelListTable
                paginatedModels={paginatedModels}
                filteredModelsLength={filteredAndSortedModels.length}
                selected={selected}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onSelectClick={handleSelectClick}
                onRequestSort={handleRequestSort}
                isSelected={isSelected}
                onExportClick={handleExportClick}
                onDuplicateClick={handleDuplicateClick}
                onRowClick={handleRowClick}
            />

            <ModelListPagination
                count={filteredAndSortedModels.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <DownloadModelDialog
                open={!!downloadModelId}
                modelId={downloadModelId || ''}
                onClose={handleCloseDownloadDialog}
            />

            <DuplicateModelDialog
                open={!!duplicateModelId}
                modelId={duplicateModelId || ''}
                onClose={handleCloseDuplicateDialog}
            />

            <BulkDeleteConfirmDialog
                open={showConfirmDialog}
                selectedCount={selected.length}
                selectedModelNames={selectedModelNames}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isDeleting={isDeleting}
            />
        </Box>
    )
}

export default ModelList
